import { z } from 'zod'

/**
 * Which path made a change to a plan, or removed an event or a plan.
 *
 * Every write that can lose someone's work says which of these it is, and the
 * database records it beside the copy it keeps (see `docs/change-tracking.md`).
 * `unknown` is what a write records when it said nothing, and it is meant to be
 * looked for: a plan changed by a path nobody stamped is the thing this list
 * exists to find.
 *
 * Deliberately not a Prisma enum. The value is written by a trigger from a
 * session setting, and an enum cast on an unrecognized string would fail the
 * save it was meant to describe. Readers parse with {@link changeSourceSchema}.
 */
export const CHANGE_SOURCES = [
  'editor',
  'collab',
  /**
   * Somebody emptied the plan on purpose: the editor's explicit clear, or a
   * live room told to allow an empty save. An empty save without this is refused.
   */
  'clear_plan',
  'import',
  'furniture_removal',
  'restore',
  'phase_delete',
  'event_delete',
  'venue_purge',
  'import_drop',
  'tenant_wipe',
  // A person's assistant, through an MCP tool (docs/mcp-plan-editing.md).
  'assistant',
  'unknown',
] as const

export const changeSourceSchema = z.enum(CHANGE_SOURCES)

export type ChangeSource = z.infer<typeof changeSourceSchema>

/**
 * Why the database kept a copy of a plan.
 *
 * - `cleared` — a write left the plan empty.
 * - `shrank` — a write removed five or more pieces, or half of them.
 * - `checkpoint` — an ordinary change, kept because no copy was newer than ten
 *   minutes, so a slow loss is recoverable too.
 * - `replaced` — most of the plan was rewritten: fewer than half its pieces came
 *   through the write unchanged (every piece moved, or swapped for others), or an
 *   import wrote it. The count can stay the same, so `shrank` would miss it.
 * - `restore` — the plan a restore replaced. Always kept, whatever the size of the
 *   change, so putting a plan back can itself be undone.
 * - `phase_deleted` — the plan was deleted along with its phase.
 */
export const REVISION_REASONS = ['cleared', 'shrank', 'replaced', 'checkpoint', 'restore', 'phase_deleted'] as const

export const revisionReasonSchema = z.enum(REVISION_REASONS)

export type RevisionReason = z.infer<typeof revisionReasonSchema>

/**
 * Whether a kept copy is a loss someone should hear about.
 *
 * Checkpoints and restores record none: a checkpoint is an ordinary change kept
 * on a timer, and a restore is the plan that was put back. A reason added to
 * {@link REVISION_REASONS} has to be classified here or the build fails, and a
 * loss is then included below without a second list to update.
 */
const REVISION_REPORTS_LOSS = {
  cleared: true,
  shrank: true,
  replaced: true,
  checkpoint: false,
  restore: false,
  phase_deleted: true,
} as const satisfies Record<RevisionReason, boolean>

type LossReason = {
  [Reason in RevisionReason]: (typeof REVISION_REPORTS_LOSS)[Reason] extends true ? Reason : never
}[RevisionReason]

export const LOSS_REASONS: readonly LossReason[] = REVISION_REASONS.filter(
  (reason): reason is LossReason => REVISION_REPORTS_LOSS[reason],
)
